import { API_BASE } from './api';
import { getConfigValue } from './config';

const GDPR_PROXY_BASE = `${API_BASE}/gdpr`;
const GDPR_API_BASE = getConfigValue('REACT_APP_GDPR_API_BASE', 'https://gdprapi.holdingthedrones.com');
const GDPR_APP_NAME = getConfigValue('REACT_APP_GDPR_APP_NAME');
const GDPR_APP_ID = getConfigValue('REACT_APP_GDPR_APP_ID');
const GDPR_APP_TOKEN = getConfigValue('REACT_APP_GDPR_APP_TOKEN');

const ACCESS_TOKEN_STORAGE_KEY = 'gdprAccessToken';
const ACCESS_TOKEN_EXP_STORAGE_KEY = 'gdprAccessTokenExp';
const IDENTITY_TOKEN_STORAGE_KEY = 'gdprIdentityToken';
const IDENTITY_TOKEN_EXP_STORAGE_KEY = 'gdprIdentityTokenExp';

const getErrorMessage = async (response, fallback) => {
  const data = await response.json().catch(() => null);
  return data?.error || data?.message || fallback;
};

const canUseWindowCrypto = () => {
  return typeof window !== 'undefined' && !!window.crypto?.subtle && typeof window.crypto.randomUUID === 'function';
};

const getConfiguredAppCredentials = () => {
  if (GDPR_APP_ID && GDPR_APP_TOKEN && GDPR_APP_NAME) {
    return {
      appId: GDPR_APP_ID,
      appToken: GDPR_APP_TOKEN,
      appName: GDPR_APP_NAME
    };
  }

  return null;
};

const getCachedAccessToken = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  const expiresAt = Number(localStorage.getItem(ACCESS_TOKEN_EXP_STORAGE_KEY));

  if (!token || !Number.isFinite(expiresAt)) {
    return null;
  }

  if (Date.now() >= expiresAt) {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXP_STORAGE_KEY);
    return null;
  }

  return token;
};

const cacheAccessToken = (token) => {
  // Backend docs state 24h JWT lifetime; refresh slightly before hard expiry.
  const expiresAt = Date.now() + (23 * 60 * 60 * 1000);
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  localStorage.setItem(ACCESS_TOKEN_EXP_STORAGE_KEY, String(expiresAt));
};

const getCachedIdentityToken = () => {
  const token = localStorage.getItem(IDENTITY_TOKEN_STORAGE_KEY);
  const expiresAt = Number(localStorage.getItem(IDENTITY_TOKEN_EXP_STORAGE_KEY));

  if (!token || !Number.isFinite(expiresAt)) {
    return null;
  }

  if (Date.now() >= expiresAt) {
    localStorage.removeItem(IDENTITY_TOKEN_STORAGE_KEY);
    localStorage.removeItem(IDENTITY_TOKEN_EXP_STORAGE_KEY);
    return null;
  }

  return token;
};

const cacheIdentityToken = (token) => {
  // Keep identity token for a short period to avoid frequent round-trips.
  const expiresAt = Date.now() + (4 * 60 * 1000);
  localStorage.setItem(IDENTITY_TOKEN_STORAGE_KEY, token);
  localStorage.setItem(IDENTITY_TOKEN_EXP_STORAGE_KEY, String(expiresAt));
};

const buildSignature = async ({ method, path, timestamp, nonce, rawBody, appToken }) => {
  const payload = [method, path, timestamp, nonce, rawBody].join('\n');
  const encoder = new TextEncoder();

  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(appToken),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));

  return Array.from(new Uint8Array(signature))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const requestGdprAccessToken = async ({ appName, appToken }) => {
  const response = await fetch(`${GDPR_API_BASE}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: appToken, name: appName })
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, `Failed to obtain GDPR token (${response.status})`));
  }

  const data = await response.json().catch(() => null);
  const accessToken = data?.accessToken;

  if (!accessToken) {
    throw new Error('GDPR token response missing accessToken');
  }

  cacheAccessToken(accessToken);
  return accessToken;
};

const getGdprAccessToken = async (credentials) => {
  const cached = getCachedAccessToken();
  if (cached) {
    return cached;
  }

  return requestGdprAccessToken(credentials);
};

const gdprSignedRequest = async ({ method, path, body = null, extraHeaders = {} }) => {
  const credentials = getConfiguredAppCredentials();
  if (!credentials || !canUseWindowCrypto()) {
    throw new Error('GDPR signed auth is not configured');
  }

  const accessToken = await getGdprAccessToken(credentials);
  const timestamp = String(Date.now());
  const nonce = crypto.randomUUID();
  const rawBody = body ? JSON.stringify(body) : '';
  const signature = await buildSignature({
    method,
    path,
    timestamp,
    nonce,
    rawBody,
    appToken: credentials.appToken
  });

  const response = await fetch(`${GDPR_API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-app-id': credentials.appId,
      'x-timestamp': timestamp,
      'x-nonce': nonce,
      'x-signature': signature,
      Authorization: `Bearer ${accessToken}`,
      ...extraHeaders
    },
    body: rawBody || undefined
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, `GDPR request failed (${response.status})`));
  }

  return response;
};

const proxyGdprRequest = async ({ method, path, body = null, bearerToken = '' }) => {
  const response = await fetch(`${GDPR_PROXY_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response, `GDPR proxy request failed (${response.status})`));
  }

  return response;
};

const requestWithFallback = async ({ method, path, body = null, bearerToken = '' }) => {
  try {
    return await gdprSignedRequest({ method, path, body });
  } catch {
    return proxyGdprRequest({ method, path, body, bearerToken });
  }
};

export async function getIdentityToken({ bearerToken = '', idToken = '' } = {}) {
  const cached = getCachedIdentityToken();
  if (cached) {
    return cached;
  }

  const endpoints = [
    `${API_BASE}/api/auth/identity-token`,
    `${API_BASE}/auth/identity-token`
  ];

  let lastError = null;

  for (const endpoint of endpoints) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        ...(idToken ? { 'X-ID-Token': idToken } : {})
      },
      body: JSON.stringify({})
    });

    if (response.ok) {
      const data = await response.json().catch(() => null);
      const token =
        data?.identityToken ||
        data?.identity_token ||
        data?.token ||
        data?.accessToken ||
        '';

      if (!token) {
        throw new Error('Identity token endpoint response missing token field');
      }

      cacheIdentityToken(token);
      return token;
    }

    lastError = new Error(await getErrorMessage(response, `Failed to obtain identity token (${response.status})`));

    if (response.status !== 404) {
      break;
    }
  }

  throw lastError || new Error('Failed to obtain identity token');
}

export async function fetchGdprOrganizations({ bearerToken = '' } = {}) {
  const response = await requestWithFallback({
    method: 'GET',
    path: '/organisation',
    bearerToken
  });

  return response.json().catch(() => []);
}

export async function fetchGdprRoles({ bearerToken = '' } = {}) {
  const response = await requestWithFallback({
    method: 'GET',
    path: '/role',
    bearerToken
  });

  return response.json().catch(() => []);
}

export async function addGdprUser(payload) {
  const endpoints = [`${GDPR_PROXY_BASE}/add/user`, `${GDPR_API_BASE}/add/user`];

  let lastError = null;

  for (const endpoint of endpoints) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      return response.json().catch(() => ({}));
    }

    lastError = new Error(await getErrorMessage(response, `Failed to add user (${response.status})`));

    if (response.status !== 404) {
      break;
    }
  }

  throw lastError || new Error('Failed to add user');
}
